import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useSettings, logoUrl } from "../context/SettingsContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Brand from "../components/Brand";
import DatabaseTransfer from "../components/DatabaseTransfer";
import { Upload, Loader2, Save, GraduationCap } from "lucide-react";

const COLORS = ["#4F46E5", "#0EA5E9", "#059669", "#DC2626", "#D97706", "#7C3AED", "#DB2777", "#0F172A"];
const BGS = ["#F8FAFC", "#F1F5F9", "#FEF3C7", "#ECFDF5", "#EFF6FF", "#FDF2F8", "#FFFFFF"];

export default function SettingsPage() {
  const { t } = useLang();
  const { settings, save, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [welcomeUploading, setWelcomeUploading] = useState(false);
  const fileRef = useRef();
  const welcomeRef = useRef();

  useEffect(() => { setForm(settings); }, [settings]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    setSaving(true);
    try {
      await save({
        school_name: form.school_name,
        primary_color: form.primary_color,
        background_color: form.background_color,
        page_style: form.page_style,
        welcome_title: form.welcome_title || "",
        welcome_subtitle: form.welcome_subtitle || "",
        welcome_image_position: form.welcome_image_position,
        welcome_text_position: form.welcome_text_position,
        welcome_text_align: form.welcome_text_align,
      });
      toast.success(t("settings.saved"));
    } catch {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success(t("settings.saved"));
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onUploadWelcome = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setWelcomeUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/welcome-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success(t("settings.saved"));
    } catch {
      toast.error("Upload failed");
    } finally {
      setWelcomeUploading(false);
      if (welcomeRef.current) welcomeRef.current.value = "";
    }
  };

  const logo = logoUrl(settings.logo_path);
  const welcomeImg = logoUrl(settings.welcome_image_path);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground">{t("settings.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="surface-card rounded-xl border border-border bg-card p-6 space-y-5">
            <div className="space-y-2">
              <Label>{t("settings.schoolName")}</Label>
              <Input value={form.school_name || ""} onChange={(e) => patch("school_name", e.target.value)} data-testid="school-name-input" />
            </div>

            <div className="space-y-2">
              <Label>{t("settings.logo")}</Label>
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-border" style={{ backgroundColor: logo ? "transparent" : "var(--brand)" }}>
                  {logo ? <img src={logo} alt="logo" className="h-full w-full object-cover" /> : <GraduationCap className="h-8 w-8 text-white" />}
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="logo-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-60">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? t("settings.uploading") : t("settings.uploadLogo")}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("settings.primaryColor")}</Label>
              <div className="flex flex-wrap items-center gap-2">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => patch("primary_color", c)} data-testid={`color-${c}`}
                    className={`h-9 w-9 rounded-lg border-2 transition-transform hover:scale-110 ${form.primary_color === c ? "border-foreground" : "border-transparent"}`}
                    style={{ backgroundColor: c }} aria-label={c} />
                ))}
                <input type="color" value={form.primary_color || "#4F46E5"} onChange={(e) => patch("primary_color", e.target.value)} data-testid="primary-color-picker" className="h-9 w-12 cursor-pointer rounded-lg border border-border bg-transparent" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("settings.backgroundColor")}</Label>
              <div className="flex flex-wrap items-center gap-2">
                {BGS.map((c) => (
                  <button key={c} onClick={() => patch("background_color", c)} data-testid={`bg-${c}`}
                    className={`h-9 w-9 rounded-lg border-2 transition-transform hover:scale-110 ${form.background_color === c ? "border-foreground" : "border-border"}`}
                    style={{ backgroundColor: c }} aria-label={c} />
                ))}
                <input type="color" value={form.background_color || "#F8FAFC"} onChange={(e) => patch("background_color", e.target.value)} data-testid="bg-color-picker" className="h-9 w-12 cursor-pointer rounded-lg border border-border bg-transparent" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("settings.pageStyle")}</Label>
              <Select value={form.page_style} onValueChange={(v) => patch("page_style", v)}>
                <SelectTrigger className="w-[220px]" data-testid="page-style-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="modern">{t("settings.styleModern")}</SelectItem>
                  <SelectItem value="classic">{t("settings.styleClassic")}</SelectItem>
                  <SelectItem value="compact">{t("settings.styleCompact")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4 rounded-xl border border-border bg-secondary/30 p-4" data-testid="welcome-settings">
              <h3 className="font-heading font-semibold text-foreground">{t("settings.welcomeSection")}</h3>

              <div className="space-y-2">
                <Label>{t("settings.welcomeImage")}</Label>
                <div className="flex items-center gap-4">
                  <div className="h-16 w-28 overflow-hidden rounded-lg border border-border bg-muted">
                    {welcomeImg ? <img src={welcomeImg} alt="welcome" className="h-full w-full object-cover" /> : null}
                  </div>
                  <input ref={welcomeRef} type="file" accept="image/*" onChange={onUploadWelcome} className="hidden" data-testid="welcome-image-input" />
                  <button onClick={() => welcomeRef.current?.click()} disabled={welcomeUploading} data-testid="upload-welcome-btn"
                    className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-60">
                    {welcomeUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {welcomeUploading ? t("settings.uploading") : t("settings.uploadWelcome")}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("settings.welcomeTitle")}</Label>
                <Input value={form.welcome_title || ""} onChange={(e) => patch("welcome_title", e.target.value)} placeholder={t("settings.welcomeTitlePh")} data-testid="welcome-title-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.welcomeSubtitle")}</Label>
                <Input value={form.welcome_subtitle || ""} onChange={(e) => patch("welcome_subtitle", e.target.value)} placeholder={t("settings.welcomeTitlePh")} data-testid="welcome-subtitle-input" />
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>{t("settings.imagePosition")}</Label>
                  <Select value={form.welcome_image_position} onValueChange={(v) => patch("welcome_image_position", v)}>
                    <SelectTrigger data-testid="welcome-image-position"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">{t("settings.posLeft")}</SelectItem>
                      <SelectItem value="right">{t("settings.posRight")}</SelectItem>
                      <SelectItem value="none">{t("settings.posNone")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.textPosition")}</Label>
                  <Select value={form.welcome_text_position} onValueChange={(v) => patch("welcome_text_position", v)}>
                    <SelectTrigger data-testid="welcome-text-position"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top">{t("settings.posTop")}</SelectItem>
                      <SelectItem value="center">{t("settings.posCenter")}</SelectItem>
                      <SelectItem value="bottom">{t("settings.posBottom")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.textAlign")}</Label>
                  <Select value={form.welcome_text_align} onValueChange={(v) => patch("welcome_text_align", v)}>
                    <SelectTrigger data-testid="welcome-text-align"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="start">{t("settings.alignStart")}</SelectItem>
                      <SelectItem value="center">{t("settings.alignCenter")}</SelectItem>
                      <SelectItem value="end">{t("settings.alignEnd")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <button onClick={onSave} disabled={saving} data-testid="save-settings-btn"
              className="brand-btn inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {t("settings.save")}
            </button>
          </div>
        </div>

        {/* live preview */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-muted-foreground">{t("settings.preview")}</div>
          <div className="surface-card overflow-hidden rounded-xl border border-border" style={{ backgroundColor: form.background_color }}>
            <div className="border-b border-border bg-card px-4 py-3">
              <Brand size="sm" />
            </div>
            <div className="p-4 space-y-3">
              <div className="rounded-lg px-4 py-2 text-sm font-medium text-white text-center" style={{ backgroundColor: form.primary_color }}>
                {form.school_name}
              </div>
              <div className="rounded-lg border border-border bg-card p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground">Twana aziz</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">Present</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DatabaseTransfer />
    </div>
  );
}
